const safeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

export const EMPTY_CLIENT = {
  name: "",
  pan: "",
  gstin: "",
  contactPerson: "",
  mobile: "",
  email: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  pinCode: "",
  remarks: "",
  status: "Active",
  latitude: "",
  longitude: "",
  geoRadiusMeters: "",
  additionalContacts: [],
  portalEnabled: false,
  portalLoginEmail: "",
  portalAccessStatus: "not_invited",
  portalAuthUid: "",
  portalLastLoginAt: null,
};

export function normalizeClient(client = {}) {
  return {
    ...EMPTY_CLIENT,
    ...client,
    contactPerson: safeText(client.contactPerson || client.contact),
    remarks: safeText(client.remarks || client.notes),
    mobile: safeText(client.mobile),
    name: safeText(client.name),
    pan: safeText(client.pan),
    gstin: safeText(client.gstin),
    email: safeText(client.email),
    address1: safeText(client.address1),
    address2: safeText(client.address2),
    city: safeText(client.city),
    state: safeText(client.state),
    pinCode: safeText(client.pinCode),
    status: safeText(client.status) || "Active",
    latitude: client.latitude === 0 || client.latitude ? client.latitude : "",
    longitude: client.longitude === 0 || client.longitude ? client.longitude : "",
    geoRadiusMeters: client.geoRadiusMeters || "",
    additionalContacts: Array.isArray(client.additionalContacts)
      ? client.additionalContacts.map((item) => ({
          name: safeText(item?.name), mobile: safeText(item?.mobile), isPrimary: Boolean(item?.isPrimary),
        })).filter((item) => item.name || item.mobile)
      : [],
    portalEnabled: Boolean(client.portalEnabled),
    portalLoginEmail: safeText(client.portalLoginEmail),
    portalAccessStatus: client.portalAuthUid ? (safeText(client.portalAccessStatus) || "invited") : (client.portalEnabled ? "not_invited" : "disabled"),
    portalAuthUid: safeText(client.portalAuthUid),
    portalLastLoginAt: client.portalLastLoginAt || null,
  };
}

export function normalizePan(value = "") {
  return safeText(value).toUpperCase();
}

export function normalizeGstin(value = "") {
  return safeText(value).toUpperCase();
}

export function validatePan(value = "") {
  const pan = normalizePan(value);
  return !pan || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan);
}

export function validateGstin(value = "") {
  const gstin = normalizeGstin(value);
  return !gstin || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]{3}$/.test(gstin);
}

export function validateClient(client) {
  const errors = {};

  if (!safeText(client.name)) errors.name = "Client name is required.";
  if (!safeText(client.pan)) errors.pan = "PAN is required.";
  else if (!validatePan(client.pan)) errors.pan = "Enter a valid PAN.";

  if (client.gstin && !validateGstin(client.gstin)) {
    errors.gstin = "Enter a valid GSTIN.";
  }

  if (client.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeText(client.email))) {
    errors.email = "Enter a valid email address.";
  }

  if (client.mobile && !/^[0-9+\-\s()]{7,15}$/.test(safeText(client.mobile))) {
    errors.mobile = "Enter a valid mobile number.";
  }
  (client.additionalContacts || []).forEach((item, index) => {
    const mobile = safeText(item?.mobile);
    if (mobile && !/^[0-9+\-\s()]{7,15}$/.test(mobile)) errors[`additionalContact_${index}`] = "Enter a valid mobile number.";
  });
  if (client.portalEnabled && !safeText(client.portalLoginEmail)) errors.portalLoginEmail = "Portal login email is required when portal access is enabled.";
  else if (client.portalLoginEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeText(client.portalLoginEmail))) errors.portalLoginEmail = "Enter a valid portal login email.";

  if (client.pinCode && !/^[0-9]{6}$/.test(safeText(client.pinCode))) {
    errors.pinCode = "PIN must contain 6 digits.";
  }

  const hasLatitude = client.latitude !== "" && client.latitude != null;
  const hasLongitude = client.longitude !== "" && client.longitude != null;
  if (hasLatitude !== hasLongitude) {
    errors.coordinates = "Enter both latitude and longitude, or leave both blank.";
  } else if (hasLatitude) {
    const latitude = Number(client.latitude);
    const longitude = Number(client.longitude);
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90 || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      errors.coordinates = "Enter valid latitude and longitude.";
    }
  }
  if (client.geoRadiusMeters) {
    const radius = Number(client.geoRadiusMeters);
    if (!Number.isFinite(radius) || radius < 50 || radius > 2000) errors.geoRadiusMeters = "Radius must be between 50 and 2,000 metres.";
  }

  return errors;
}

export function toFirestoreClient(client) {
  return {
    name: safeText(client.name),
    pan: normalizePan(client.pan),
    gstin: normalizeGstin(client.gstin),
    contactPerson: safeText(client.contactPerson),
    contact: safeText(client.contactPerson),
    mobile: safeText(client.mobile),
    email: safeText(client.email),
    address1: safeText(client.address1),
    address2: safeText(client.address2),
    city: safeText(client.city),
    state: safeText(client.state),
    pinCode: safeText(client.pinCode),
    remarks: safeText(client.remarks),
    notes: safeText(client.remarks),
    status: safeText(client.status) || "Active",
    latitude: client.latitude === "" || client.latitude == null ? null : Number(client.latitude),
    longitude: client.longitude === "" || client.longitude == null ? null : Number(client.longitude),
    geoRadiusMeters: client.geoRadiusMeters === "" || client.geoRadiusMeters == null ? null : Number(client.geoRadiusMeters),
    additionalContacts: (client.additionalContacts || []).map((item) => ({
      name: safeText(item?.name), mobile: safeText(item?.mobile), isPrimary: Boolean(item?.isPrimary),
    })).filter((item) => item.name || item.mobile),
    portalEnabled: Boolean(client.portalEnabled),
    portalLoginEmail: safeText(client.portalLoginEmail).toLowerCase(),
    portalAccessStatus: Boolean(client.portalEnabled) ? (safeText(client.portalAuthUid) ? (safeText(client.portalAccessStatus) || "invited") : "not_invited") : "disabled",
    portalAuthUid: safeText(client.portalAuthUid),
    portalLastLoginAt: client.portalLastLoginAt || null,
  };
}

export function toFirestoreClientPartial(client = {}, presentFields = []) {
  const full = toFirestoreClient(client); const allowed = new Set(presentFields || []); const payload = {};
  Object.keys(full).forEach((key) => { if (allowed.has(key)) payload[key] = full[key]; });
  if (allowed.has("contactPerson")) payload.contact = full.contact;
  if (allowed.has("remarks")) payload.notes = full.notes;
  return payload;
}
